import { AttributeKind } from '@prisma/client';
import { CatalogVariant, VocabularyAttribute } from '../../types/catalog';
import { orderedAttributes } from './catalogFilters';

/** Axis for whatever the label says that attribute facts do not (imports). */
export const LABEL_AXIS_KEY = '__label__';
const LABEL_SEPARATOR = /\s*[·|,]\s*|\s+-\s+/;

export interface ChooserOption {
  value: string;
  numericValue: number | null;
}

export interface ChooserAxis {
  key: string;
  label: string;
  options: ChooserOption[];
  /** Label-derived axes carry each variant's value explicitly. */
  valueByVariant?: Record<string, string>;
}

export type ChooserSelection = Record<string, string>;

export function variantValue(
  variant: CatalogVariant,
  axis: ChooserAxis,
): string | null {
  if (axis.valueByVariant) return axis.valueByVariant[variant.id] ?? null;
  return variant.attributes[axis.key]?.label ?? null;
}

function sortOptions(
  options: ChooserOption[],
  attribute: VocabularyAttribute | undefined,
) {
  const sortOrder = new Map(
    attribute?.values.map(v => [v.label.toLowerCase(), v.sortOrder]) ?? [],
  );
  return [...options].sort((a, b) => {
    if (
      attribute?.kind === AttributeKind.NUMERIC &&
      a.numericValue !== null &&
      b.numericValue !== null
    ) {
      return a.numericValue - b.numericValue;
    }
    const sa = sortOrder.get(a.value.toLowerCase());
    const sb = sortOrder.get(b.value.toLowerCase());
    if (sa !== undefined && sb !== undefined && sa !== sb) return sa - sb;
    return a.value.localeCompare(b.value, undefined, { numeric: true });
  });
}

function attributeAxes(
  variants: CatalogVariant[],
  attributes: VocabularyAttribute[],
): ChooserAxis[] {
  return orderedAttributes(attributes).flatMap((attribute): ChooserAxis[] => {
    const byValue = new Map<string, ChooserOption>();
    variants.forEach(variant => {
      const fact = variant.attributes[attribute.key];
      if (fact && !byValue.has(fact.label)) {
        byValue.set(fact.label, {
          value: fact.label,
          numericValue: fact.numericValue,
        });
      }
    });
    if (byValue.size < 2) return [];
    return [
      {
        key: attribute.key,
        label: attribute.label,
        options: sortOptions([...byValue.values()], attribute),
      },
    ];
  });
}

/** The label with every segment already covered by an attribute axis removed. */
function residualLabel(variant: CatalogVariant, axes: ChooserAxis[]) {
  const label = variant.variantLabel ?? '';
  const covered = new Set(
    axes
      .map(axis => variantValue(variant, axis)?.toLowerCase())
      .filter((v): v is string => !!v),
  );
  const residual = label
    .split(LABEL_SEPARATOR)
    .map(part => part.trim())
    .filter(part => part && !covered.has(part.toLowerCase()))
    .join(' · ');
  return residual || label;
}

function isAmbiguous(variants: CatalogVariant[], axes: ChooserAxis[]) {
  const seen = new Set<string>();
  return variants.some(variant => {
    const key = axes
      .map(axis => variantValue(variant, axis) ?? '')
      .join('\u0000');
    if (seen.has(key)) return true;
    seen.add(key);
    return false;
  });
}

/** One axis per varying attribute, plus a label axis when those don't pin a variant. */
export function buildChooserAxes(
  variants: CatalogVariant[],
  attributes: VocabularyAttribute[],
): ChooserAxis[] {
  const axes = attributeAxes(variants, attributes);
  if (variants.length < 2 || !isAmbiguous(variants, axes)) return axes;

  const valueByVariant = Object.fromEntries(
    variants
      .map(variant => [variant.id, residualLabel(variant, axes)] as const)
      .filter(([, value]) => !!value),
  );
  const values = [...new Set(Object.values(valueByVariant))];
  if (values.length < 2) return axes;

  return [
    ...axes,
    {
      key: LABEL_AXIS_KEY,
      label: 'Options',
      options: sortOptions(
        values.map(value => ({ value, numericValue: null })),
        undefined,
      ),
      valueByVariant,
    },
  ];
}

export function matchingVariants(
  variants: CatalogVariant[],
  axes: ChooserAxis[],
  selection: ChooserSelection,
  ignoreAxis?: string,
): CatalogVariant[] {
  const active = axes.filter(
    axis => axis.key !== ignoreAxis && selection[axis.key] !== undefined,
  );
  return variants.filter(variant =>
    active.every(axis => variantValue(variant, axis) === selection[axis.key]),
  );
}

/** The single variant the selection pins down, or null while it is ambiguous. */
export function resolveVariant(
  variants: CatalogVariant[],
  axes: ChooserAxis[],
  selection: ChooserSelection,
): CatalogVariant | null {
  const matches = matchingVariants(variants, axes, selection);
  if (matches.length === 1) return matches[0];
  if (axes.every(axis => selection[axis.key]) && matches.length)
    return matches[0];
  return null;
}

export interface OptionState extends ChooserOption {
  available: boolean;
  /** Shown when every variant behind the option shares one price. */
  price: number | null;
}

export function optionStates(
  variants: CatalogVariant[],
  axes: ChooserAxis[],
  axis: ChooserAxis,
  selection: ChooserSelection,
): OptionState[] {
  const others = matchingVariants(variants, axes, selection, axis.key);
  return axis.options.map(option => {
    const behind = others.filter(v => variantValue(v, axis) === option.value);
    const prices = new Set(behind.map(v => v.price));
    return {
      ...option,
      available: behind.length > 0,
      price: prices.size === 1 ? behind[0].price : null,
    };
  });
}

export function selectionForVariant(
  variant: CatalogVariant,
  axes: ChooserAxis[],
): ChooserSelection {
  return Object.fromEntries(
    axes
      .map(axis => [axis.key, variantValue(variant, axis)] as const)
      .filter((entry): entry is readonly [string, string] => !!entry[1]),
  );
}
